import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useScriptCreation } from '../../contexts/ScriptCreationContext';
import { FaRocket, FaUndo, FaMagic, FaSpinner, FaSave } from 'react-icons/fa';
import { Icon } from '../../components/common/Icon';
import MeditationDisplay from '../../components/meditation/MeditationDisplay';
import { config } from '../../config';
import { useArtGeneration } from '../../hooks/useArtGeneration';
import { useUser } from '../../contexts/UserContext';
import { authService } from '../../services/auth';
import { useCoins } from '../../contexts/UserContext';
import { HelpButton } from '../../components/common/HelpButton';

const helpContent = `
<div class="alert info">
First off, now that you are here, listen to your mediatation and make sure you are happy with it.  If not, you can still go back and make changes.</br></br>
<ul>
<li>Give your meditation a good title and description, so that others can find it.</li>
<li>All published meditations have an image. Describe the visual representation you'd like for this meditation, and press the "Generate Image" button to generate an image. The first image
generation is free, but subsequent generations will cost coins.</li>
<li>Select all of the tags that apply to your meditation.  You must select at least 2 tags.</li>
<li>Click on the gear icon to adjust the auto-loop setting, and to publish anonymously or privately.</li>
</ul>
</div>

`;
const Container = styled.div`
  
  margin: 0 auto;
  padding: 20px;
`;

const PreviewSection = styled.div`
  margin-bottom: 40px;
`;

const PreviewTitle = styled.h3`
  color: #ffffff;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: #ffffff;
  margin-bottom: 10px;
  font-size: 24px;
`;

const Description = styled.p`
  color: #cccccc;
  font-size: 16px;
  margin-bottom: 20px;
`;

const Form = styled.form`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #333;
`;

const FormSection = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &::placeholder {
    color: #888;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  min-height: 50px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &::placeholder {
    color: #888;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const RevertButton = styled.button`
  background: #666;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;

  &:hover {
    background: #777;
  }
  
  &:disabled {
    background: #444;
    cursor: not-allowed;
  }
`;

const GenerateButton = styled.button`
  background: #9c27b0;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;

  &:hover {
    background: #7b1fa2;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.button<{ $selected: boolean }>`
  background: ${props => props.$selected ? '#007bff' : '#333'};
  color: white;
  border: 1px solid ${props => props.$selected ? '#007bff' : '#444'};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? '#0056b3' : '#444'};
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #007bff;
  font-size: 14px;
  margin-top: 10px;
  
  span {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background: #2a1a1a;
  border: 1px solid #5a1a1a;
  border-radius: 6px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 30px;
`;

const PublishButtonRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ValidationMessage = styled.div`
  color: #ffa726;
  font-size: 14px;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PublishButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 18px;
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CheckboxLabel = styled.label`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  line-height: 1.4;
  
  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PrivacyMessage = styled.p`
  color: #ffa726;
  font-size: 13px;
  line-height: 1.4;
  margin: 0px 0 4px 38px;
  font-style: italic;
`;

const PrivacyTooltip = styled.p`
  color: #888;
  font-size: 12px;
  margin: 0px 0 4px 38px;
  line-height: 1.3;
`;

const AVAILABLE_TAGS = [
  'Sitting', 'Lying', 'Walking', 'Eyes open', 'Eyes closed', 'Relaxing',
  'Thinking', 'Focus', 'Sleeping', 'Dreaming', 'Out of body', 'Spiritual', 'Nature',
  'Non-Spiritual', 'Body Scan', 'Story', 'Manifestation', 'Visualization',
  'Affirmations', 'Mantras', 'Healing', 'Music','Ambient','Instrumental',
  'Delta', 'Theta', 'Alpha', 'Beta','Gamma', 
  'Binaural Beats', 'Amplitude Modulation', 'Frequency Modulation',
  'Stereo Modulation', 'Isochronic Tones'
];

const Publish: React.FC = () => {
  const { refreshCoins } = useCoins();
  const { 
    scriptMetadata, 
    isGeneratingMetadata, 
    metadataError,
    scriptName,
    setScriptName,
    scriptFinalAudioFile,
    scriptDbId,
    scriptArtFile,
    setScriptArtFile,
    publishTitle,
    setPublishTitle,
    publishDescription,
    setPublishDescription,
    publishImagePrompt,
    setPublishImagePrompt,
    publishTags,
    setPublishTags,
    isPrivate,
    setIsPrivate,
    autoLoop,
    setAutoLoop,
    isAnonymous,
    setIsAnonymous,
    isProtected,
    setIsProtected,
    savePublishData
  } = useScriptCreation();

  const { user } = useUser();
  const { 
    generateArt, 
    isGenerating: isGeneratingArt, 
    generatedImage, 
    error: artError, 
    progress: artProgress 
  } = useArtGeneration();

  // Form state - now managed by context
  // title, description, imagePrompt, selectedTags are now publishTitle, publishDescription, publishImagePrompt, publishTags

  // Art generation state
  const [isSavingArt, setIsSavingArt] = useState(false);
  const [artSaveError, setArtSaveError] = useState<string | null>(null);
  const [hasSuccessfullySaved, setHasSuccessfullySaved] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);


  // Track original title (before metadata) for revert functionality
  const [originalTitle, setOriginalTitle] = useState('');
  const hasSetOriginalTitle = useRef(false);
  const hasSetTitleFromMetadata = useRef(false);

  // Initialize with script name and store as original title
  useEffect(() => {
    if (scriptName && !hasSetOriginalTitle.current && !scriptMetadata) {
      setPublishTitle(scriptName);
      setOriginalTitle(scriptName);
      hasSetOriginalTitle.current = true;
    }
  }, [scriptName, scriptMetadata, setPublishTitle]);

  // Populate form when metadata is available (this takes precedence)
  useEffect(() => {
    if (scriptMetadata) {
      // Store original title if not already set
      if (!hasSetOriginalTitle.current && scriptName) {
        setOriginalTitle(scriptName);
        hasSetOriginalTitle.current = true;
      }
      
      // Only set title from metadata if it hasn't been set before (prevents overriding user edits)
      if (!hasSetTitleFromMetadata.current) {
        setPublishTitle(scriptMetadata.title);
        hasSetTitleFromMetadata.current = true;
      }
      
      // Always update other fields from metadata
      setPublishDescription(scriptMetadata.description);
      setPublishImagePrompt(scriptMetadata.image_prompt);
      setPublishTags(scriptMetadata.tags);
    }
  }, [scriptMetadata, scriptName, setPublishTitle, setPublishDescription, setPublishImagePrompt, setPublishTags]);

  // Sync publish title with script name for ScriptCreationLayout
  useEffect(() => {
    if (publishTitle && publishTitle !== scriptName) {
      setScriptName(publishTitle);
    }
  }, [publishTitle, scriptName, setScriptName]);

  const handleTagToggle = (tag: string) => {
    const newTags = publishTags.includes(tag) 
      ? publishTags.filter(t => t !== tag)
      : [...publishTags, tag];
    setPublishTags(newTags);
  };

  const handleRevertTitle = () => {
    setPublishTitle(originalTitle);
  };

  const handleGenerateImage = async () => {
    if (!publishImagePrompt.trim() || !scriptDbId || !user) {
      return;
    }

    // Check if this is a regeneration (already has art)
    const isRegeneration = scriptArtFile !== null;
    const coinCost = isRegeneration ? 150 : 0;

    // Check if user has enough coins for regeneration
    if (isRegeneration && user.coins < coinCost) {
      setArtSaveError(`Insufficient coins. You need ${coinCost} coins but only have ${user.coins}.`);
      return;
    }

    try {
      setArtSaveError(null);
      setHasSuccessfullySaved(false);
      setSavedImageUrl(null);
      
      // Generate the art
      await generateArt({
        prompt: publishImagePrompt,
        scriptId: scriptDbId, // Include the required scriptId
        resolution: 'landscape',
        quality: 'high'
      });
      await refreshCoins();
      
    } catch (error) {
      console.error('Art generation error:', error);
      setArtSaveError('Failed to generate art. Please try again.');
    }
  };

  // Save generated art to script
  const saveGeneratedArt = async (artS3Key: string) => {
    if (!scriptDbId || hasSuccessfullySaved || isSavingArt) {
      console.log('Skipping art save:', { scriptDbId, hasSuccessfullySaved, isSavingArt });
      return;
    }

    try {
      setIsSavingArt(true);
      setArtSaveError(null);

      // The new backend returns just the S3 key (filename)
      // No need to extract paths anymore
      console.log('Saving art with S3 key:', artS3Key);

      const response = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/save-art`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          artFile: artS3Key
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save art');
      }

      const result = await response.json();
      
      // Update local state
      setScriptArtFile(result.script.art_file);
      
      // Mark as successfully saved to prevent re-saving
      setHasSuccessfullySaved(true);
      setSavedImageUrl(null);
      
      console.log(`Art saved successfully. Cost: ${result.coinCost} coins`);
      
    } catch (error) {
      console.error('Save art error:', error);
      setArtSaveError(error instanceof Error ? error.message : 'Failed to save art');
    } finally {
      setIsSavingArt(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await savePublishData();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!scriptDbId) {
      console.error('No script ID available for publishing');
      return;
    }
    
    setIsPublishing(true);
    
    try {
      // First save any pending changes
      await savePublishData();
      
      // Call the publish endpoint
      const response = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to publish meditation');
      }
      
      // Redirect to the meditation page
      window.location.href = `/meditation/${scriptDbId}`;
      
    } catch (error) {
      console.error('Publish error:', error);
      alert(error instanceof Error ? error.message : 'Failed to publish meditation. Please try again.');
      setIsPublishing(false);
    }
  };

  // Comprehensive validation for publishing
  const validation = useMemo(() => {
    if (!publishTitle.trim()) {
      return { canPublish: false, message: "You must enter a title" };
    }
    
    if (publishDescription.trim().length < 20) {
      return { canPublish: false, message: `You must enter a longer description (${publishDescription.trim().length}/20 characters)` };
    }
    
    if (!scriptFinalAudioFile) {
      return { canPublish: false, message: "You must first finalize your audio on the Audio page" };
    }
    
    if (!scriptArtFile) {
      return { canPublish: false, message: "You must first generate an image" };
    }
    
    if (publishTags.length < 2) {
      return { canPublish: false, message: `You must select at least 2 tags (currently ${publishTags.length} selected)` };
    }
    
    return { canPublish: true, message: "" };
  }, [publishTitle, publishDescription, scriptFinalAudioFile, scriptArtFile, publishTags.length]);
  
  const canPublish = validation.canPublish;

  // Get audio URL for the finalized audio file
  const audioUrl = scriptFinalAudioFile ? `${scriptFinalAudioFile}` : undefined;
  
  // Auto-save generated art when it's ready (only once per generation)
  const [savedImageUrl, setSavedImageUrl] = useState<string | null>(null);
  
  useEffect(() => {
    if (generatedImage && !isSavingArt && generatedImage !== savedImageUrl && !hasSuccessfullySaved) {
      setSavedImageUrl(generatedImage);
      saveGeneratedArt(generatedImage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedImage, isSavingArt, savedImageUrl, hasSuccessfullySaved]);

  return (
    <Container>
      <Header>
        <Title>Publish Your Meditation</Title>
        <Description>
          Complete the details below to publish your meditation script to the community.
        </Description>
      </Header>

      {/* Preview Section */}
      <PreviewSection>
        <PreviewTitle>Preview</PreviewTitle>
        <MeditationDisplay
          title={publishTitle || 'Your Meditation Title'}
          description={publishDescription || 'Your meditation description will appear here...'}
          tags={publishTags}
          audioUrl={audioUrl}
          scriptId={scriptDbId}
          artUrl={scriptArtFile ? `${scriptArtFile}` : undefined}
        />
      </PreviewSection>

      <Form onSubmit={handlePublish}>
        <FormSection>
          <ButtonRow>
            <Label>Title</Label>
            {scriptMetadata && (
              <RevertButton 
                type="button" 
                onClick={handleRevertTitle}
                disabled={publishTitle === originalTitle}
              >
                <Icon icon={FaUndo} /> Revert to "{originalTitle}"
              </RevertButton>
            )}
          </ButtonRow>
          <Input
            type="text"
            value={publishTitle}
            onChange={(e) => setPublishTitle(e.target.value)}
            placeholder="Enter a compelling title for your meditation"
            required
          />
        </FormSection>

        <FormSection>
          <Label>Description</Label>
          <TextArea
            value={publishDescription}
            onChange={(e) => setPublishDescription(e.target.value)}
            placeholder="Describe what makes this meditation special and what listeners can expect"
            required
          />
        </FormSection>

        <FormSection>
          <ButtonRow>
            <Label>Image Prompt</Label>
            <GenerateButton 
              type="button" 
              onClick={handleGenerateImage}
              disabled={!publishImagePrompt.trim() || isGeneratingArt || isSavingArt || !scriptDbId}
            >
              {isGeneratingArt || isSavingArt ? (
                <>
                  <Icon icon={FaSpinner} />
                  {isGeneratingArt ? artProgress : 'Saving...'}
                </>
              ) : (
                <>
                  <Icon icon={FaMagic} />
                  {scriptArtFile ? (<>Re-generate Image 150 <img src="/images/YMcoin.png" alt="coin" style={{width: '16px', height: '16px', marginLeft: '2px', verticalAlign: 'middle'}} /></>) : 'Generate Image'}
                </>
              )}
            </GenerateButton>
          </ButtonRow>
          <TextArea
            value={publishImagePrompt}
            onChange={(e) => setPublishImagePrompt(e.target.value)}
            placeholder="Describe the visual representation you'd like for this meditation"
          />
        </FormSection>

        <FormSection>
          <Label>Tags</Label>
          <TagsContainer>
            {AVAILABLE_TAGS.map(tag => (
              <Tag
                key={tag}
                type="button"
                $selected={publishTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              >
                {tag}
              </Tag>
            ))}
          </TagsContainer>
        </FormSection>

        <FormSection>
          <CheckboxContainer>
            <Checkbox
              id="auto-loop-checkbox"
              type="checkbox"
              checked={autoLoop}
              onChange={(e) => setAutoLoop(e.target.checked)}
            />
            <CheckboxLabel htmlFor="auto-loop-checkbox">
              Automatically loop (this is meant for meditations that you would listen to on repeat while sleeping/working)
            </CheckboxLabel>
          </CheckboxContainer>
          
          <CheckboxContainer>
            <Checkbox
              id="anonymous-checkbox"
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
            />
            <CheckboxLabel htmlFor="anonymous-checkbox">
              Publish Anonymously (Publish publicly without your username attached, Ex. You don't want your substance abuse cessation meditation linked to you)
            </CheckboxLabel>
          </CheckboxContainer>
          
          <CheckboxContainer>
            <Checkbox
              id="private-checkbox"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              disabled={!user?.user_permissions?.private_scripts}
            />
            <CheckboxLabel 
              htmlFor="private-checkbox"
              className={!user?.user_permissions?.private_scripts ? 'disabled' : ''}
            >
              Keep this meditation private (Only you can use it)
            </CheckboxLabel>
          </CheckboxContainer>
          
          
          
          {!user?.user_permissions?.private_scripts && (
            <PrivacyTooltip>
              Not available in your current subscription tier.
            </PrivacyTooltip>
          )}
          
          {isPrivate && user?.user_permissions?.private_scripts && (
            <PrivacyMessage>
              Please consider publishing this script publicly, so that others have the opportunity to benefit from it, or be inspired by it!
            </PrivacyMessage>
          )}

          <CheckboxContainer>
            <Checkbox
              id="protected-checkbox"
              type="checkbox"
              checked={isProtected}
              onChange={(e) => setIsProtected(e.target.checked)}
              disabled={!user?.user_permissions?.protected_scripts}
            />
            <CheckboxLabel 
              htmlFor="protected-checkbox"
              className={!user?.user_permissions?.protected_scripts ? 'disabled' : ''}
            >
              Make this meditation protected (Cannot be customized by others)
            </CheckboxLabel>
          </CheckboxContainer>

            {!user?.user_permissions?.protected_scripts && (
            <PrivacyTooltip>
              Not available in your current subscription tier.
            </PrivacyTooltip>
          )}
          


        </FormSection>

        {isGeneratingMetadata && (
          <LoadingIndicator>
            <Icon icon={FaSpinner} />
            Generating metadata...
          </LoadingIndicator>
        )}

        {metadataError && (
          <ErrorMessage>
            {metadataError}
          </ErrorMessage>
        )}

        {(artError || artSaveError) && (
          <ErrorMessage>
            {artError || artSaveError}
          </ErrorMessage>
        )}

        <ButtonContainer>
          <PublishButtonRow>
            <SaveButton 
              type="button" 
              onClick={handleSave}
              disabled={isSaving}
            >
              <Icon icon={FaSave} /> 
              {isSaving ? 'Saving...' : 'Save'}
            </SaveButton>
            
            <PublishButton 
              type="submit" 
              disabled={!canPublish || isPublishing}
            >
              <Icon icon={isPublishing ? FaSpinner : FaRocket} /> 
              {isPublishing ? 'Publishing...' : 'Publish Meditation'}
            </PublishButton>
          </PublishButtonRow>
          
          {!canPublish && validation.message && (
            <ValidationMessage>
              {validation.message}
            </ValidationMessage>
          )}
        </ButtonContainer>
      </Form>

      <HelpButton
        title="Publish Your Meditation"
        content={helpContent}
        position="fixed-bottom-right"
        />
    </Container>
  );
};

export default Publish; 